import { useMemo } from "react";
import { useFetcher } from "~/hooks/useFetcher";

interface TagEntityResponse {
  data: {
    fullyQualifiedName: string;
    displayName?: string;
    classification: { displayName?: string };
  }[];
}

const { VITE_CATALOG_API } = import.meta.env;

const useTagDisplayMappings = () => {
  const { data: tagData } = useFetcher<TagEntityResponse>(
    `${VITE_CATALOG_API}/om/v1/tags?limit=1000000`,
    {
      keepPreviousData: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  return useMemo<Record<string, { classification?: string; tagName?: string } | undefined>>(() => {
    if (tagData) {
      return tagData.data.reduce((acc, tag) => {
        return {
          ...acc,
          [tag.fullyQualifiedName]: {
            tagName: tag.displayName,
            classification: tag.classification.displayName,
          },
        };
      }, {});
    }
    return {};
  }, [tagData]);
};

export const useTagDisplayName = (tagFQN: string) => {
  const mappings = useTagDisplayMappings();

  const displayMapping = mappings[tagFQN];
  // Fall back to pulling from FQN if no display name mapping available
  const [classificationFallback, tagNameFallback] = tagFQN.split(".");

  const classificationDisplayName = displayMapping?.classification || classificationFallback;
  const tagDisplayName = displayMapping?.tagName || tagNameFallback;

  return [classificationDisplayName, tagDisplayName].join(" / ");
};

export default useTagDisplayMappings;
